import React from "react";

import BarraDeSincronizacion from "./app/BarraDeSincronizacion.jsx";
import DisclaimerBar from "./app/DisclaimerBar.jsx";
import OfflineBar from "./app/OfflineBar.jsx";
import PantallaDeIngreso from "./app/PantallaDeIngreso.jsx";
import UpdateToast from "./app/UpdateToast.jsx";
import { SELLO_PROPIO, comprobarSello } from "./app/selloDeVersion.js";
import { ADENTRO, AFUERA, estadoDeSesion, salir } from "./services/sesion/sesion.js";
import { VISTAS, puedeAbrirVista, vistaDePerfil } from "./domain/entrada.js";
import VistaDeGestion from "./features/gestion/VistaDeGestion.jsx";
import HeliCalcLegacy from "./features/legacy/HeliCalcLegacy.jsx";

/**
 * Cascarón de la aplicación.
 *
 * Fase 0: el prototipo legacy se monta entero y sin modificar debajo del
 * cascarón. Todo lo que se agregue acá (disclaimer, estado de red, control de
 * actualizaciones) es infraestructura que el legacy no tenía y que no depende
 * de cómo esté escrito por dentro.
 *
 * A medida que avancen las fases, los módulos van saliendo del legacy y
 * entrando como rutas propias en este mismo cascarón, hasta que
 * HeliCalcLegacy quede vacío y se borre.
 */
export default function App() {
  /* ═══ IR A LA ADMINISTRACIÓN DESDE ARRIBA ═══

     El botón vive en la barra del aviso legal —arriba a la derecha, a la vista
     en todas las pantallas— y la pestaña activa la maneja el legacy, que es
     quien dibuja las herramientas. Se le pide prestado su `setTab` una sola vez
     y se guarda acá: es lo mínimo para que el cascarón pueda mandar a una
     pantalla sin hacerse cargo del estado interno del legacy. El día que el
     legacy se vacíe —que es el plan— esto se borra y la pestaña la maneja este
     archivo. */
  const irA = React.useRef(null);
  const registrarIr = React.useCallback((f) => { irA.current = f; }, []);

  /* ═══ TRES ESTADOS, NO DOS ═══

     1. **`consultando`** — todavía no se sabe. No se muestra nada más que un
        aviso: mostrar la entrada y sacarla doscientos milisegundos después se
        lee como un error.
     2. **`afuera`** — hay servidor y hay base, y nadie entró. Va la pantalla de
        entrada.
     3. **`adentro`** — hay sesión. **El perfil decide la vista**: el piloto a la
        operación, el resto a gestión.

     Y `sin-servidor` no es ninguno de los tres: cuando no hay con quién hablar,
     **la aplicación abre igual, en la vista de operación**, como funcionó
     siempre. Confundirlo con «afuera» deja al piloto mirando un formulario que
     nunca va a funcionar — y esta aplicación está hecha para trabajar sin
     backend, en un iPad, en un helipuerto. La entrada es lo único que necesita
     servidor. */
  const [sesion, setSesion] = React.useState({ estado: "consultando" });

  /* ═══ LA VISTA PEDIDA A MANO ═══

     El perfil decide con qué vista se ABRE, y eso no se toca. Pero un
     administrador de empresa también vuela: su perfil lo deja en gestión y la
     matriz le da `vuelos.escribir`, así que la vista de operación le corresponde
     por derecho y no tenía forma de abrirla.

     Es un estado de la pantalla y NO una preferencia guardada, a propósito:
     cada vez que se entra, se entra por donde dice el perfil. Guardarlo
     convertiría una excepción de un rato en una configuración que nadie
     revisa. */
  const [vistaPedida, setVistaPedida] = React.useState(null);

  React.useEffect(() => {
    let vivo = true;
    estadoDeSesion().then((r) => { if (vivo) setSesion(r); });
    return () => { vivo = false; };
  }, []);

  React.useEffect(() => {
    comprobarSello().then((r) => {
      if (r.estado !== "al-dia") {
        console.info("[HeliCalc] sello:", r.estado, "| esta copia:", r.propio, "| servidor:", r.servidor);
      }
    });
  }, []);

  const cerrarSesion = React.useCallback(async () => {
    await salir();
    setSesion({ estado: AFUERA });
  }, []);

  if (sesion.estado === "consultando") {
    return (
      <div className="ing ing--centrado">
        <p className="ing__lema" role="status">Cargando…</p>
      </div>
    );
  }

  if (sesion.estado === AFUERA) {
    return (
      <PantallaDeIngreso
        alEntrar={(usuario) => setSesion({ estado: ADENTRO, usuario })}
        sello={SELLO_PROPIO}
      />
    );
  }

  const usuario = sesion.usuario || null;
  const vistaPorPerfil = usuario ? vistaDePerfil(usuario.perfil) : VISTAS.VUELO;

  /* Se cambia de vista solo si el perfil la tiene permitida. El botón no se le
     muestra a quien no puede, pero la comprobación va acá igual. */
  const vista = usuario && vistaPedida && puedeAbrirVista(usuario.perfil, vistaPedida)
    ? vistaPedida
    : vistaPorPerfil;

  const vuelveAGestion = usuario !== null && vistaPedida === VISTAS.VUELO;

  /* ═══ UN PERFIL QUE NO EXISTE NO RECIBE NINGUNA DE LAS DOS VISTAS ═══

     No es exagerado: la vista de gestión muestra la operación entera de una
     empresa, y dársela por descarte es un control de acceso que falla abierto.

     ═══ ESTA PANTALLA ES UN DIAGNÓSTICO, NO UN CALLEJÓN ═══

     Antes decía «la sesión abrió con el perfil «{usuario.perfil}»». Cuando el
     perfil llega vacío —que es el caso que ocurre— eso se dibuja como un ««»» sin
     nada adentro: el peor mensaje posible, porque no dice qué pasó ni qué mirar.
     Costó una ronda de ida y vuelta con el operador.

     Que el perfil llegue vacío tiene DOS causas, y la primera es de despliegue:

     1. **El servidor quedó más viejo que la aplicación.** La respuesta de la
        entrada se arma con el perfil que devuelve la consulta; si esa consulta no
        lo devuelve, la clave no llega al navegador y `JSON.stringify` la borra sin
        dejar rastro. Pasó con la e140: se subió el sitio y no el servidor.
     2. **El usuario no tiene perfil asignado** en la base.

     Se dicen las dos, en ese orden, porque la primera es la que pasa. */
  if (usuario && vistaPorPerfil === null) {
    const sinPerfil = !usuario.perfil;

    return (
      <div className="ing ing--centrado">
        <main className="ing__tarjeta">
          <h1 className="ing__titulo">
            {sinPerfil ? "La sesión vino sin perfil" : "Perfil desconocido"}
          </h1>

          {sinPerfil ? (
            <>
              <p className="ing__nota">
                El servidor abrió la sesión de <strong>{usuario.nombre}</strong> pero no
                mandó con qué perfil. No se abre ninguna vista hasta saber qué puede
                hacer esa cuenta, y adivinarlo sería un control de acceso que falla
                abierto.
              </p>
              <p className="ing__nota">
                Lo más probable es que <strong>el servidor esté más atrasado que la
                aplicación</strong>: se subió el sitio y no los archivos del servidor.
                Revisa que <code>server/</code> esté al día — con Git es automático.
                Si ya lo está, entonces esta cuenta no tiene perfil asignado en la
                base: se lo asigna quien administra.
              </p>
            </>
          ) : (
            <p className="ing__nota">
              La sesión abrió con el perfil «{usuario.perfil}», que no es uno de los
              perfiles de la plataforma. Pedile a quien administra que lo revise: no se
              abre ninguna vista hasta saber qué puede hacer.
            </p>
          )}

          <p className="ing__nota ing__nota--sello">
            Esta copia es <span className="ing__sello">{SELLO_PROPIO}</span>
          </p>

          <button type="button" className="ing__entrar" onClick={cerrarSesion}>
            Cerrar sesión
          </button>
        </main>
      </div>
    );
  }

  if (vista === VISTAS.GESTION) {
    return (
      <div className="hc-shell">
        <VistaDeGestion
          usuario={usuario}
          alSalir={cerrarSesion}
          sello={SELLO_PROPIO}
          alAbrirVuelo={
            puedeAbrirVista(usuario.perfil, VISTAS.VUELO)
              ? () => setVistaPedida(VISTAS.VUELO)
              : null
          }
        />
        <OfflineBar />
        <BarraDeSincronizacion activa={usuario !== null} perfil={usuario?.perfil || null} />
        <UpdateToast />
      </div>
    );
  }

  /* ═══ EL BOTÓN DE ADMINISTRACIÓN, SEGÚN HAYA SESIÓN O NO ═══

     Sin sesión —el servidor de desarrollo, o un servidor sin base de datos— no
     hay perfil y no hay a dónde ir: queda el camino de siempre, que abre la
     pantalla de empresa dentro de la vista de operación.

     Con sesión, ese botón y la vista de gestión llevan AL MISMO componente
     (`PanelAdministracion`): eran dos puertas a la misma habitación puestas una
     al lado de la otra. Queda una sola, la de gestión. */
  const administraSinSesion = !usuario;

  return (
    <div className="hc-shell">
      <DisclaimerBar
        alAdministrar={administraSinSesion ? () => irA.current?.("empresa") : undefined}
        alVolver={vuelveAGestion ? () => setVistaPedida(null) : null}
        usuario={usuario}
        alSalir={usuario ? cerrarSesion : null}
      />
      <OfflineBar />
      <BarraDeSincronizacion activa={usuario !== null} perfil={usuario?.perfil || null} />
      <main className="hc-shell__content">
        <HeliCalcLegacy alRegistrarIr={registrarIr} />
      </main>
      <UpdateToast />
    </div>
  );
}
